import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCheck, MessageSquare, Send } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { fmtStamp as stamp, isStaff, viewOnlyTrip } from "@/lib/format";
import { Textarea } from "@/components/ui/textarea";

const OWNER_ROLES = ["broker", "executive"];

function Bubble({ m, mine }) {
  const staffMsg = !OWNER_ROLES.includes(m.author_role);
  return (
    <div className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid={`trip-message-${m.id}`}>
      <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-line break-words ${mine ? "on-navy bg-[#1E3A8A] text-white" : "bg-[#5B7FD1]/10 text-zinc-100 border border-white/10"}`}>
        {m.body}
      </div>
      <p className="text-[10px] text-zinc-500 mt-1">
        {m.author_name || (staffMsg ? "Flight department" : "Broker")}{staffMsg ? " · Flight team" : ""} · {stamp(m.created_at)}
      </p>
    </div>
  );
}

export default function TripChat({ trip, onChanged, option = null }) {
  const api = useApi();
  const { me } = useMe();
  const staff = isStaff(me);
  const viewOnly = viewOnlyTrip(me, trip);
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const box = useRef(null);
  const section = useRef(null);
  const input = useRef(null);
  const [params] = useSearchParams();
  const replyLink = !option && params.get("reply") === "1";
  const loaded = msgs !== null;

  useEffect(() => {
    if (!replyLink || !loaded) return;
    section.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    input.current?.focus({ preventScroll: true });
  }, [replyLink, loaded]);

  const markAnswered = async () => {
    try { await api.post(`/trips/${trip.id}/messages/answered`); toast.success("Marked as answered"); onChanged?.(); } catch (err) { toast.error(errMsg(err)); }
  };

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/trips/${trip.id}/messages`, { params: option ? { option_id: option.id } : {} }); setMsgs(data); } catch { setMsgs((m) => m || []); }
  }, [api, trip.id, option]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [msgs?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/messages`, { body: text, option_id: option?.id });
      setMsgs((m) => [...(m || []), data]);
      setText("");
      onChanged?.();
      toast.success(staff ? "Reply sent. The broker has been notified." : "Question sent. The flight department has been notified.");
    } catch (err) { toast.error(errMsg(err)); } finally { setSending(false); }
  };

  const tid = option ? `option-${option.rank}-chat` : "trip-chat";
  return (
    <section ref={section} id={option ? undefined : "trip-chat"} className={option ? "mt-4 border-t border-white/10 pt-4" : "lux-card m-card p-5 scroll-mt-28"} data-testid={tid}>
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare size={15} className="text-[#5B7FD1]" />
        <p className="eyebrow !mb-0 flex-1">{option ? `Questions about Option ${option.rank}` : staff || viewOnly ? "Broker questions" : "Ask the flight department"}</p>
        {staff && !option && trip.awaiting_reply && (
          <button type="button" onClick={markAnswered} className="inline-flex items-center gap-1 text-[0.6rem] tracking-[0.16em] uppercase text-zinc-400 hover:text-[#5B7FD1] transition-colors"
            title="Use this if you answered the broker by email" data-testid="trip-chat-mark-answered-btn">
            <CheckCheck size={13} /> Mark answered
          </button>
        )}
      </div>
      <div ref={box} className="space-y-3 max-h-80 overflow-y-auto pr-1" data-testid={`${tid}-messages`}>
        {msgs === null && <p className="text-xs text-zinc-500">Loading…</p>}
        {msgs?.length === 0 && (
          <p className="text-xs text-zinc-500" data-testid={`${tid}-empty`}>
            {option ? (staff || viewOnly ? "No questions about this option yet." : "Ask the flight department anything about this aircraft.") : staff || viewOnly ? "No questions from the broker yet." : "Have a question about this trip? Ask here and the flight department will reply."}
          </p>
        )}
        {msgs?.map((m) => <Bubble key={m.id} m={m} mine={m.author_id === me?.id} />)}
      </div>
      {!viewOnly && <form onSubmit={send} className="mt-4 flex flex-col gap-2">
        <Textarea ref={input} value={text} onChange={(e) => setText(e.target.value)} maxLength={3000} className="bg-[#0d0f14] border-white/10 min-h-[72px] text-sm"
          placeholder={staff ? "Reply to the broker…" : option ? `Ask about the ${option.aircraft_type}…` : "Type your question…"} data-testid={`${tid}-input`}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(e); }} />
        <button type="submit" disabled={sending || !text.trim()} className="frame-btn gold self-end !py-2.5 !px-4" data-testid={`${tid}-send-btn`}>
          <Send size={13} /> {sending ? "Sending…" : staff ? "Send reply" : "Ask question"}
        </button>
      </form>}
    </section>
  );
}
